import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Mail, Copy, Send, AlertCircle, CheckCircle2, ListPlus } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../services/api';
import BrokerBadge from './BrokerBadge';

const TEMPLATES = {
  underinsured: ({ asset, gap, gap_pct, formatCurrency }) => ({
    subject: `Increase insurance cover - ${asset.name}`,
    body:
`Hello,

I would like to increase the insured value for the following item in my collection. The current insured value is materially below the current market value, leaving me exposed to a partial-loss shortfall.

Asset: ${asset.name}
Category: ${asset.category}
Current market value: ${formatCurrency(asset.current_value)}
Current insured value: ${formatCurrency(asset.insured_value || 0)}
Cover gap: ${formatCurrency(gap)} (${gap_pct}% underinsured)

Please send me a revised quotation reflecting the higher sum insured at your earliest convenience. I am happy to provide any updated documentation you require (recent valuation, condition report, photographs).

Kind regards,`
  }),
  uninsured: ({ asset, formatCurrency }) => ({
    subject: `Insurance quotation request - ${asset.name}`,
    body:
`Hello,

I would like to obtain insurance cover for the following item, which is currently uninsured.

Asset: ${asset.name}
Category: ${asset.category}
Current market value: ${formatCurrency(asset.current_value)}

Please could you let me know what information you require to provide a quotation? I can supply purchase documentation, a recent valuation, condition photographs, and storage details.

Kind regards,`
  }),
  expiring: ({ asset, days_left, formatCurrency }) => ({
    subject: `Policy renewal - ${asset.name} (${days_left <= 0 ? 'expired' : `expires in ${days_left} days`})`,
    body:
`Hello,

The insurance policy for the asset below is about to lapse. Please could you initiate the renewal and let me know if anything needs to change for the upcoming term.

Asset: ${asset.name}
Category: ${asset.category}
Current market value: ${formatCurrency(asset.current_value)}
Current insured value: ${formatCurrency(asset.insured_value || 0)}
Policy expiry: ${asset.insurance_expiry || 'unknown'}
Provider: ${asset.provider || asset.insurance_provider || '(your broker)'}

If a re-valuation is required for renewal, I am happy to arrange it.

Kind regards,`
  }),
  concentration: ({ asset, pct_of_portfolio, formatCurrency }) => ({
    subject: `Review - ${asset.name} (concentration risk)`,
    body:
`Hello,

I'd like to schedule a short call to review the role of the asset below in my overall portfolio. It currently represents ${pct_of_portfolio}% of my total holdings, which exceeds my chosen concentration tolerance.

Asset: ${asset.name}
Category: ${asset.category}
Current market value: ${formatCurrency(asset.current_value)}
Share of portfolio: ${pct_of_portfolio}%

I'd like to discuss whether partial liquidation, hedging, or rebalancing is appropriate.

Kind regards,`
  }),
};

const ISSUE_LABELS = {
  underinsured:  { title: 'Fix underinsurance', icon: AlertCircle, tone: 'red' },
  uninsured:    { title: 'Request cover',       icon: AlertCircle, tone: 'amber' },
  expiring:     { title: 'Renew policy',         icon: AlertCircle, tone: 'amber' },
  concentration: { title: 'Review concentration', icon: AlertCircle, tone: 'amber' },
};

export default function FixThisModal({ open, onClose, issue, asset, formatCurrency, defaultRecipient }) {
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState(false);
  const [advisor, setAdvisor] = useState(null);
  const [assignToAdvisor, setAssignToAdvisor] = useState(() => {
    const saved = localStorage.getItem('assagart_assign_to_advisor');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    if (!open) return;
    api.getMyAdvisor()
      .then(r => setAdvisor(r.data?.advisor || null))
      .catch(() => setAdvisor(null));
  }, [open]);

  useEffect(() => {
    localStorage.setItem('assagart_assign_to_advisor', String(assignToAdvisor));
  }, [assignToAdvisor]);

  useEffect(() => {
    if (!open || !asset) return;
    const tmpl = TEMPLATES[issue]?.({
      asset,
      gap: asset.gap || 0,
      gap_pct: asset.gap_pct || 0,
      days_left: asset.days_left ?? 0,
      pct_of_portfolio: asset.pct_of_portfolio || 0,
      formatCurrency,
    }) || { subject: '', body: '' };
    setSubject(tmpl.subject);
    setBody(tmpl.body);
    setTo(defaultRecipient || asset.provider_email || '');
    // do NOT reset assignToAdvisor - we now respect the user's saved preference
  }, [open, issue, asset, formatCurrency, defaultRecipient]);

  if (!asset) return null;
  const meta = ISSUE_LABELS[issue] || { title: 'Fix this', icon: AlertCircle, tone: 'gold' };
  const Icon = meta.icon;

  const mailtoUrl = () => {
    const recipient = to.trim();
    const qs = new URLSearchParams();
    if (subject) qs.set('subject', subject);
    if (body) qs.set('body', body);
    const params = qs.toString();
    return `mailto:${encodeURIComponent(recipient)}${params ? `?${params}` : ''}`;
  };

  const handleSend = () => {
    if (!to.trim()) {
      toast.error('Add a recipient email first');
      return;
    }
    window.location.href = mailtoUrl();
  };

  const handleCopy = async () => {
    const text = `To: ${to}\nSubject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Draft copied to clipboard');
    } catch {
      toast.error('Could not copy - please select and copy manually');
    }
  };

  const handleSaveToQueue = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await api.createTicket({
        asset_id: asset.asset_id || asset.id,
        issue,
        title: subject || `${meta.title} - ${asset.name}`,
        detail: body,
        recipient_email: to || null,
        assigned_partner_id: advisor && assignToAdvisor ? advisor.id : null,
      });
      toast.success(advisor && assignToAdvisor ? `Sent to ${advisor.full_name} and saved to your queue` : 'Saved to your Action queue');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#0F0F0F] border-[#262626] max-w-2xl" data-testid="fix-this-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <Icon size={18} className={meta.tone === 'red' ? 'text-red-400' : 'text-amber-400'} />
            {meta.title} - {asset.name}
          </DialogTitle>
        </DialogHeader>

        <div className="bg-gold/5 border border-gold/20 rounded p-3 mb-3 text-[11px] text-zinc-300 leading-relaxed">
          We've drafted the email for you - review, edit anything you'd like, then open it in your mail
          client. Nothing is sent automatically, and we never store this draft.
        </div>

        <div className="space-y-3">
          <div>
            <Label className="text-muted-foreground text-xs flex items-center justify-between gap-2">
              <span>To (your broker / advisor)</span>
              <BrokerBadge email={to} />
            </Label>
            <Input
              type="email"
              value={to}
              onChange={(e) => { const v = e.target.value; setTo(v); }}
              className="luxury-input"
              placeholder="broker@example.com"
              data-testid="fix-this-to"
            />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">Subject</Label>
            <Input
              value={subject}
              onChange={(e) => { const v = e.target.value; setSubject(v); }}
              className="luxury-input"
              data-testid="fix-this-subject"
            />
          </div>
          <div>
            <Label className="text-muted-foreground text-xs">Message</Label>
            <textarea
              value={body}
              onChange={(e) => { const v = e.target.value; setBody(v); }}
              rows={12}
              className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-sm text-white font-mono leading-relaxed focus:outline-none focus:border-gold/40"
              data-testid="fix-this-body"
            />
          </div>
        </div>

        {advisor && (
          <label
            htmlFor="fix-this-assign-advisor"
            className="mt-3 flex items-start gap-3 rounded border border-gold/25 bg-gold/5 p-3 cursor-pointer hover:bg-gold/10 transition-colors"
            data-testid="fix-this-advisor-toggle"
          >
            <input
              id="fix-this-assign-advisor"
              type="checkbox"
              checked={assignToAdvisor}
              onChange={(e) => setAssignToAdvisor(e.target.checked)}
              className="mt-0.5 accent-gold"
              data-testid="fix-this-advisor-checkbox"
            />
            <div className="text-[11px] leading-snug">
              <span className="text-gold">Send to my advisor when I save this</span>
              <span className="block text-muted-foreground mt-0.5">
                {advisor.full_name}{advisor.company ? ` - ${advisor.company}` : ''}. They will see this in their inbox.
              </span>
            </div>
          </label>
        )}

        <DialogFooter className="gap-2 flex-row flex-wrap sm:justify-end">
          <Button
            type="button"
            variant="ghost"
            onClick={handleSaveToQueue}
            disabled={saving}
            className="text-amber-400 hover:text-amber-300 hover:bg-amber-500/10"
            data-testid="fix-this-save-queue"
          >
            <ListPlus size={14} className="mr-2" /> {saving ? 'Saving...' : 'Save to action queue'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={handleCopy}
            className="text-muted-foreground hover:text-white"
            data-testid="fix-this-copy"
          >
            <Copy size={14} className="mr-2" /> Copy draft
          </Button>
          <Button
            type="button"
            onClick={handleSend}
            className="bg-gradient-to-r from-gold/80 to-gold text-black font-semibold hover:from-gold hover:to-gold/90"
            data-testid="fix-this-send"
          >
            <Send size={14} className="mr-2" /> Open in mail client
          </Button>
        </DialogFooter>

        <p className="text-[10px] text-muted-foreground/60 text-center mt-2 flex items-center justify-center gap-1">
          <CheckCircle2 size={10} className="text-emerald-400" />
          This draft is built locally in your browser. It is never sent to our servers.
        </p>
      </DialogContent>
    </Dialog>
  );
}
